import { v4 as uuid } from 'uuid';

import {
  Action,
  ActionId,
  ActionType,
  ActionUpdate,
  applyActionUpdate,
  getDefaultAction,
} from 'client/app/apps/disco-prototype/state/actions/actions';
import { LiquidSet, LiquidSetId } from 'client/app/apps/disco-prototype/state/liquidSet';
import { getUniqueName } from 'client/app/apps/disco-prototype/state/names';
import { reorderItems } from 'client/app/apps/disco-prototype/state/reorder';

export type StepId = string;

export type Step = {
  id: StepId;
  name: string;
  description: string;
  // target is the liquid set that will be acted upon by each of the actions in the step.
  // Where Values in the actions select columns, they should select columns from
  // target.table.
  target?: LiquidSet;
  device?: string;
  actions: Action[];
};

/**
 * removes all references to the liquid set from the steps
 */
export const removeLiquidSet = (steps: Step[], toRemove: LiquidSetId) => {
  steps.forEach(step => {
    if (step.target?.id === toRemove) {
      step.target = undefined;
    }
  });
};

export const getDefaultStep = (steps: Step[]): Step => {
  return {
    id: uuid(),
    name: getUniqueName('New Step', steps),
    description: '',
    actions: [],
  };
};

export type StepUpdate =
  | UpdateStep
  | CreateAction
  | UpdateAction
  | ReorderActions
  | DeleteAction;

type UpdateStep = {
  type: 'update';
  name?: string;
  description?: string;
  target?: LiquidSetId;
  device?: string;
};

type CreateAction = {
  type: 'createAction';
  actionType: ActionType;
};

type UpdateAction = {
  type: 'updateAction';
  toUpdate: ActionId;
  update: ActionUpdate;
};

type ReorderActions = {
  type: 'reorderActions';
  newOrder: ActionId[];
};

type DeleteAction = {
  type: 'deleteAction';
  toDelete: ActionId;
};

export const applyStepUpdate = (
  step: Step,
  liquidSets: LiquidSet[],
  update: StepUpdate,
) => {
  switch (update.type) {
    case 'update': {
      step.name = update.name ?? step.name;
      step.description = update.description ?? step.description;
      step.device = update.device ?? step.device;
      if (update.target) {
        step.target = liquidSets.find(ls => ls.id === update.target) ?? step.target;
      }
      break;
    }
    case 'createAction': {
      step.actions.push(getDefaultAction(update.actionType));
      break;
    }
    case 'updateAction': {
      const toUpdate = step.actions.find(a => a.id === update.toUpdate);
      toUpdate && applyActionUpdate(toUpdate, liquidSets, step.target, update.update);
      break;
    }
    case 'reorderActions': {
      step.actions = reorderItems(step.actions, update.newOrder);
      break;
    }
    case 'deleteAction': {
      step.actions = step.actions.filter(a => a.id !== update.toDelete);
      break;
    }
    default: {
      const _: never = update;
      console.error(`unexpected update ${_}`);
    }
  }
};
